import { useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import { useStore } from 'reactflow';

interface HelperLinesProps {
  horizontal?: number;
  vertical?: number;
}

const canvasStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  position: 'absolute',
  top: 0,
  left: 0,
  pointerEvents: 'none',
  zIndex: 10,
};

// Alignment-guide accent — matches the editor's node-selection blue.
const LINE_COLOR = '#3b82f6';

/**
 * Renders the smart alignment guide lines as a full-canvas overlay, mapping the
 * flow-space coordinates from getHelperLines() into screen space via the live
 * React Flow viewport transform.
 */
export function HelperLines({ horizontal, vertical }: HelperLinesProps) {
  // Select primitives individually so default (Object.is) equality avoids
  // needless re-renders without pulling in zustand's shallow comparator.
  const width = useStore((s) => s.width);
  const height = useStore((s) => s.height);
  const transform = useStore((s) => s.transform);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const dpi = window.devicePixelRatio || 1;
    canvas.width = width * dpi;
    canvas.height = height * dpi;
    ctx.scale(dpi, dpi);
    ctx.clearRect(0, 0, width, height);
    ctx.strokeStyle = LINE_COLOR;
    ctx.lineWidth = 1;

    const [tx, ty, zoom] = transform;

    if (typeof vertical === 'number') {
      const x = vertical * zoom + tx;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }

    if (typeof horizontal === 'number') {
      const y = horizontal * zoom + ty;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
  }, [width, height, transform, horizontal, vertical]);

  return <canvas ref={canvasRef} className="react-flow__helperlines" style={canvasStyle} />;
}

export default HelperLines;
